import { useState, useEffect } from 'react';
import { Box, Menu, styled } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import FHButton from './FHButton';
import LogoIcon from '../svgs/LogoIcon';
import { getThemeColor } from '../../utils/common';
import { Themes } from '../../constants';
import { FORTA_WHITE, FORTA_BLACK, FORTA_BLUE } from '../../utils/colors';

import type { ButtonProps, MenuProps as MMenuProps } from '@mui/material';
import type { ReactElement } from 'react';

interface ButtonDropdownProps extends ButtonProps {
  close?: boolean;
  triggerOpen?: boolean;
  colorTheme?: Themes;
  buttonText?: string;
  disabled?: boolean;
  hideMenu?: boolean;
  showMenuTriangle?: boolean;
  coloredBackground?: boolean;
  preventCloseOnBodyClick?: boolean;
  svgHoverStyles?: any;
  dataTestId?: string;
  popupDataTestId?: string;
  // to open the dropdown programmatically, pass btnId and handle trigger open
  // if you are using button renderer, be sure to pass same id to the custom button as you pass in btnId prop
  btnId?: string;
  onClose?: () => void;
  buttonRenderer?: () => ReactElement;
  onDropDownClick?: () => void;
}

interface MenuProps extends MMenuProps {
  showMenuTriangle?: boolean;
  coloredBackground?: boolean;
  colorTheme?: Themes;
  svgHoverStyles?: any;
}

const StyledMenu = styled(
  ({ sx = {}, ...rest }: MenuProps) => (
    <Menu
      elevation={1}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      {...rest}
      sx={{ border: `1px solid ${FORTA_BLUE.light1}`, ...sx }}
    />
  ),
  {
    shouldForwardProp: (prop) =>
      prop !== 'colorTheme' &&
      prop !== 'coloredBackground' &&
      prop !== 'svgHoverStyles' &&
      prop !== 'showMenuTriangle',
  },
)<MenuProps>(({ colorTheme, coloredBackground, svgHoverStyles, showMenuTriangle, sx }) => ({
  '& .MuiPaper-root': {
    width: 'auto',
    marginTop: 5,
    backgroundColor: coloredBackground ? getThemeColor(colorTheme).light3 : FORTA_WHITE,
    color: FORTA_BLACK,
    fontSize: '1em',
    borderRadius: '10px 0 10px 10px',
    border: `1px solid ${getThemeColor(colorTheme).light1}`,
    flexGrow: 1,
    boxShadow: `0px 4px 4px ${getThemeColor(colorTheme).light2}`,
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      padding: '0.4em 2em',
    },
    overflow: showMenuTriangle ? 'visible' : 'hidden',
    '&::before': showMenuTriangle
      ? {
          borderBottom: `10px solid ${getThemeColor(Themes.Primary).light3}`,
          borderLeft: '10px solid transparent',
          borderRight: '10px solid transparent',
          width: 0,
          height: 0,
          content: '""',
          position: 'absolute',
          right: '36px',
          top: '-10px',
        }
      : {},
    ...sx,
  },
  '& .MuiMenuItem-root:hover': {
    backgroundColor: getThemeColor(colorTheme).primary,
    color: FORTA_WHITE,
    '& .MuiBox-root': {
      '& > svg': {
        ...svgHoverStyles,
      },
    },
  },
  '& .MuiMenuItem-root.Mui-selected': {
    backgroundColor: getThemeColor(colorTheme).primary,
    color: FORTA_WHITE,
  },
  '& .MuiMenuItem-root.Mui-selected:hover': {
    backgroundColor: getThemeColor(colorTheme).primary,
  },
  '& .MuiButtonBase-root': {
    padding: 0,
  },
}));

function FButtonDropdown(props: ButtonDropdownProps) {
  const {
    close,
    triggerOpen,
    buttonText,
    children,
    disabled = false,
    hideMenu = false,
    showMenuTriangle = false,
    colorTheme = Themes.Primary,
    coloredBackground,
    preventCloseOnBodyClick,
    svgHoverStyles = {},
    btnId,
    buttonRenderer,
    onClose,
    sx,
    dataTestId,
    popupDataTestId,
    onDropDownClick,
  } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (!close) return;
    handleClose();
  }, [close]);

  useEffect(() => {
    if (!triggerOpen || !btnId) return;
    const button = document.getElementById(btnId);
    setAnchorEl(button);
  }, [triggerOpen, btnId]);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (onDropDownClick) onDropDownClick();
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }

    event.stopPropagation();
  };
  const handleClose = (preventClose?: boolean) => {
    if (preventClose) return;
    setAnchorEl(null);
    if (onClose) {
      onClose();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: 'auto' }} data-testid={dataTestId}>
      {buttonRenderer ? (
        <Box
          sx={{
            opacity: disabled ? 0.5 : 1,
            width: 'fit-content',
            '&:hover': {
              cursor: disabled ? 'default' : 'pointer',
            },
          }}
          onClick={disabled ? undefined : handleClick}
        >
          {buttonRenderer()}
        </Box>
      ) : (
        <FHButton
          id={btnId}
          fullWidth
          variant="contained"
          onClick={handleClick}
          disabled={disabled}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            borderRadius: '10px',
            '& .MuiTypography-root': {
              marginTop: '4px',
            },
          }}
          colorTheme={colorTheme}
          endIcon={children ? <KeyboardArrowDownIcon /> : null}
        >
          {buttonText ? buttonText : <LogoIcon color={FORTA_WHITE} />}
        </FHButton>
      )}
      {!hideMenu && (
        <StyledMenu
          data-testid={popupDataTestId}
          colorTheme={colorTheme}
          svgHoverStyles={svgHoverStyles}
          anchorEl={anchorEl}
          coloredBackground={coloredBackground}
          showMenuTriangle={showMenuTriangle}
          sx={sx}
          open={open}
          onClose={() => handleClose()}
          onClick={() => handleClose(preventCloseOnBodyClick)}
        >
          {children}
        </StyledMenu>
      )}
    </Box>
  );
}

export default FButtonDropdown;
